/**
 * `button` — a coloured button drawn from the column's settings, on every row.
 *
 * The label and the colour are **settings**, not cell data, which is why `text` is non-empty
 * on every cell of the column, clicked or not `[cvs§2 button]`. Pressing it is not a value
 * write: it fires the automation trigger `button_clicked` and bumps the click counter, so the
 * cell raises a `cell-action` request instead of committing a value.
 *
 * Captured settings dialog: 400 × 472, titled *Button column settings*, with a *Set up*
 * affordance for the automation (`editors/button/tier2-capture.json`).
 */
import {
  BUTTON_DEFAULT_COLOR,
  buttonLabel,
  getColumnType,
  type ButtonSettings,
  type ButtonValue,
} from '@elizmonday/shared';
import { requestCellAction } from '../cell-action';
import { defineColumnUi } from '../types';
import { ButtonSettingsDialog } from './SettingsDialog';

const shared = getColumnType('button');

export const buttonColumnUi = defineColumnUi<ButtonValue, ButtonSettings>({
  type: 'button',
  icon: 'columnGeneric',
  gallery: { group: 'advanced', keywords: ['button', 'action', 'trigger', 'automation'] },
  /** P-022: `4098059368` `Start Request` (`width: null`) renders **140** px in monday. */
  defaultWidth: 140,
  align: 'center',
  groupable: false,
  /** One click presses it; there is no editor to open on a second one. */
  activateOnFirstClick: true,

  Cell: ({ settings, column, item, value }) => (
    <button
      type="button"
      className="ez-cell-button"
      style={{ backgroundColor: settings.color ?? BUTTON_DEFAULT_COLOR }}
      data-testid={`button-${column.id}-${item.id}`}
      title={value?.clicks ? `Clicked ${String(value.clicks)}×` : undefined}
      onClick={(event) => {
        event.stopPropagation();
        requestCellAction('button-click', column.id, String(item.id));
      }}
    >
      {buttonLabel(settings)}
    </button>
  ),

  SettingsDialog: ButtonSettingsDialog,

  fromClipboard() {
    throw new Error('the button column is not writable');
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf() {
    return [];
  },
});
